<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="modalTitle"
    size="medium"
    @close="handleClose"
  >
    <template #body>
      <div class="mt-5 flex w-full flex-col gap-3">
        <div class="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <BaseShowEditDatePickerv2
              v-model:date="thirdPartyCostPayment.emission_date"
              :label="$t('attributes.date')"
              :errors="errors.emission_date"
              today-default
              edition-mode
              required
            />
          </div>
          <div>
            <BaseShowEditDatePickerv2
              v-model:date="thirdPartyCostPayment.payment_date"
              :label="$t('attributes.payment_date')"
              :errors="errors.payment_date"
              edition-mode
              required
            />
          </div>
          <div>
            <SelectPaymentModes
              v-model="thirdPartyCostPayment.payment_mode"
              :errors="errors.payment_mode"
              :label="$t('global.payment_method')"
              edition-mode
              required
            />
          </div>
          <template
            v-if="
              thirdPartyCostPayment.payment_mode === PaymentMode.CHECK ||
              thirdPartyCostPayment.payment_mode === PaymentMode.LEVY
            "
          >
            <div>
              <BaseShowEditInput
                v-model="thirdPartyCostPayment.check_number"
                :label="$t('attributes.check_number')"
                name="check_number"
                class="col-start-1"
                :errors="errors.check_number"
                :edition-mode="true"
              />
            </div>
          </template>
          <SelectComponent
            v-if="
              ![PaymentMode.CASH, PaymentMode.CREDIT_NOTE].includes(
                thirdPartyCostPayment.payment_mode
              )
            "
            v-model="thirdPartyCostPayment.bank_account_id"
            :label="$t('attributes.bank')"
            name="bank_account_id"
            :edition-mode="true"
            :values="initialMetadata?.models?.bankAccounts"
            :errors="errors?.bank_account_id"
            :searchable="true"
          />
          <div class="mt-3 col-start-1 col-span-2">
            <BaseShowEditTextArea
              v-model="thirdPartyCostPayment.remarks"
              name="remarks"
              :label="$t('attributes.remarks')"
              :edition-mode="true"
              :errors="errors?.remarks"
            />
          </div>
          <div class="col-span-2 col-start-1 my-10 flex flex-col gap-y-1">
            <div
              v-for="item in invoiceItems"
              :key="item.id"
              class="flex flex-row items-center rounded bg-gray-100 p-1 px-2"
            >
              <div class="flex-1">
                {{ item.name?.[$i18n.locale] ?? item.name }}
              </div>
              <div class="shrink-0">
                <FontAwesomeIcon
                  :icon="[
                    'fal',
                    updateInvoiceItems.includes(item.id)
                      ? 'square-check'
                      : 'square'
                  ]"
                  class="text-primary"
                  @click="handleUpdateInvoiceItems(item.id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <BaseButton
        :title="mainButtonTitle"
        :icon="mainButtonIcon"
        :display-loader="formSubmitting"
        :disabled="formSubmitting"
        color="primary"
        @click="submitForm"
      />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { PaymentMode } from '@/assets/enums'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import SelectPaymentStatuses from '@c/InitialStateFormComponent/Enums/SelectPaymentStatuses.vue'
import BaseShowEditDatePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDatePickerv2.vue'
import SelectUser from '@c/InitialStateFormComponent/SelectUser.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import SelectPaymentTypes from '@c/InitialStateFormComponent/Enums/SelectPaymentTypes.vue'
import SelectPaymentModes from '@c/InitialStateFormComponent/Enums/SelectPaymentModes.vue'
import SelectPaymentOrigins from '@c/InitialStateFormComponent/Enums/SelectPaymentOrigins.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseInput from '@c/addf-package/components/BaseInput/BaseInput.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseShowEditTextArea from "@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue";

export default {
  name: 'ThirdPartyCostPaymentFormModal',
  components: {
    BaseShowEditTextArea,
    FontAwesomeIcon,
    BaseShowEditInput,
    SelectComponent,
    SelectPaymentModes,
    BaseButton,
    BaseShowEditDatePickerv2,
    BaseCenteredModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    invoiceItems: {
      type: Array,
      required: true
    },
    thirdPartyCostPayment: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'update:is-modal-open', 'refresh-list'],
  data() {
    return {
      formSubmitting: false,
      PaymentMode: PaymentMode,
      bankCreation: [
        {
          id: 1,
          name: this.$t('global.existing')
        },
        {
          id: 2,
          name: this.$t('global.new_bank')
        }
      ],
      bankType: 1,
      amountErrors: null,
      editBank: this.paymentId ? 0 : 1,
      change_status_dossier: false,
      originalAmount: 0,
      updateInvoiceItems: []
    }
  },
  computed: {
    ...mapGetters({
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata',
      invoice_doc_types: 'metadata/getInvoiceDocTypes',
      invoice: 'invoicing/getInvoice'
    }),
    modalTitle() {
      const amount = this.invoiceItems
        .filter((item) => this.updateInvoiceItems.includes(item.id))
        .reduce((total, item) => total + (parseFloat(item.final_price_total_including_vat) || 0), 0)
        .toFixed(2)
      return this.$t('validation.settlement_title', { amount })
    },
    mainButtonTitle() {
      return this.thirdPartyCostPayment?.id
        ? this.$t('global.modify_settlement')
        : this.$t('global.save_settlement')
    },
    mainButtonIcon() {
      return this.thirdPartyCostPayment?.id ? 'pen' : 'paper-plane'
    },
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    bankAccounts() {
      return this.invoice?.bank_accounts ?? []
    },
    filteredBanks() {
      const filteredBanks = this.initialMetadata?.models.banks.filter(
        (bank) => !bank?.single_use
      )

      const sortedBanks = filteredBanks.sort((bankA, bankB) => {
        const nameA = (bankA?.name || '').toLowerCase()
        const nameB = (bankB?.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
      return sortedBanks
    }
  },
  watch: {
    invoiceItems: {
      immediate: true,
      handler(items) {
        this.updateInvoiceItems = items.map((item) => item.id)
      }
    }
  },
  methods: {
    ...mapActions({
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID',
      processFetchNewPaymentAction: 'invoicing/fetchNewPayment',
      processStoreNewPaymentAction: 'invoicing/storeNewPayment',
      processStoreNewThirdPartyCostPaymentAction:
        'invoicing/storeNewThirdPartyCostPayment',
      processFetchPaymentByIdAction: 'invoicing/fetchPaymentById',
      processUpdateThirdPartyCostPaymentByIdAction:
        'invoicing/updateThirdPartyCostPaymentById',
      processFetchNewBankAction: 'invoicing/storeNewBank'
    }),
    handleUpdateInvoiceItems(item_id) {
      if (this.updateInvoiceItems.includes(item_id)) {
        this.updateInvoiceItems = this.updateInvoiceItems.filter(
          (item) => item !== item_id
        )
      } else {
        this.updateInvoiceItems.push(item_id)
      }
    },
    submitForm() {
      if (!this.thirdPartyCostPayment.id) {
        this.processStoreNewThirdPartyCostPaymentAction({
          ...this.thirdPartyCostPayment,
          amount: undefined,
          invoiceItems: this.updateInvoiceItems
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_added_to_invoice'))
            this.$emit('refresh-list', _response)
            this.$emit('close')
            this.updateInvoiceItems = []
          })
          .catch((_reason) => {
            this.formSubmitting = false
          })
      } else {
        this.processUpdateThirdPartyCostPaymentByIdAction({
          ...this.thirdPartyCostPayment,
          amount: undefined,
          invoiceItems: this.updateInvoiceItems
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_updated'))
            this.$emit('refresh-list', _response)
            this.$emit('close')
            this.updateInvoiceItems = []
          })
          .catch((_reason) => {
            this.formSubmitting = false
          })
      }
    },
    changeBankOptions(v) {
      return (this.bankType = v)
    },
    toggleRadioEdit() {
      this.editBank = !this.editBank
    },
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>
